'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Play, Search, X, Clock, Film, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { isTourMediaRegenAvailable, requestTourMediaRegen } from './utils/tourMediaRegen';
import MediaViewer from '@/components/shared/media/MediaViewer';
import type { MediaFile } from '@/components/shared/media/MediaLibrary';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

/**
 * GUIDED TOUR VIDEO LIBRARY
 *
 * Fullscreen modal listing every published auto-generated tour walkthrough
 * video, grouped by module and searchable. Playback reuses the shared
 * MediaViewer — the same player used on /resources — so users never have to
 * leave the app to watch a guide.
 *
 * Open it by dispatching:
 *   window.dispatchEvent(new CustomEvent('guided-tour-video-library-open', {
 *     detail: { moduleKey?, flowId? }
 *   }))
 * `flowId` auto-opens that flow's video in the player when it exists.
 */

export const TOUR_VIDEO_LIBRARY_EVENT = 'guided-tour-video-library-open';

export interface TourVideoLibraryDetail {
  moduleKey?: string;
  flowId?: string;
}

interface TourVideoItem {
  id: number;
  sku: string;
  title: string;
  description: string | null;
  file_url: string;
  thumbnail_url: string | null;
  duration: number | null;
  module_key: string | null;
  module_name: string | null;
  module_icon: string | null;
  flow_id: string | null;
  published_at: string | null;
  updated_at: string | null;
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function toMediaFile(video: TourVideoItem): MediaFile {
  return {
    id: video.id,
    client_id: 0,
    user_id: 0,
    file_name: video.title,
    file_path: video.file_url,
    file_url: video.file_url,
    file_type: 'mp4',
    file_size: 0,
    mime_type: 'video/mp4',
    content_type: 'video',
    media_purpose: 'generic',
    title: video.title,
    description: video.description || '',
    alt_text: video.title,
    tags: ['user-guide', 'guided-tour'],
    thumbnail_url: video.thumbnail_url ?? undefined,
    duration: video.duration ?? undefined,
    category: 'User Guides',
    status: 'published',
    is_public: true,
    // Real publish timestamps from published_resources — fabricating
    // `new Date()` here made every video look brand-new in the viewer.
    published_at: video.published_at ?? video.updated_at ?? new Date(0).toISOString(),
    is_archived: false,
    usage_count: 0,
    view_count: 0,
    download_count: 0,
    created_at: video.updated_at ?? video.published_at ?? new Date(0).toISOString(),
    updated_at: video.updated_at ?? video.published_at ?? new Date(0).toISOString(),
    share_token: null,
  };
}

export function GuidedTourVideoLibrary() {
  const [isOpen, setIsOpen] = useState(false);
  const [videos, setVideos] = useState<TourVideoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [pendingFlowId, setPendingFlowId] = useState<string | null>(null);

  // Dev+root only: re-record a single flow's video/audio on demand. There is
  // no scheduler — nothing replaces published media unless a root user
  // explicitly requests it.
  const mediaRegenAvailable = isTourMediaRegenAvailable();
  const [regenKey, setRegenKey] = useState<string | null>(null);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/guided-tours/videos');
      const result = await response.json();
      if (result.success) {
        setVideos(result.videos || []);
      }
    } catch (error) {
      console.error('[TourVideoLibrary] Failed to load videos:', error);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, []);

  const openVideo = useCallback((video: TourVideoItem) => {
    setSelectedMedia(toMediaFile(video));
    setViewerOpen(true);
  }, []);

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<TourVideoLibraryDetail>).detail;
      setIsOpen(true);
      if (!loaded && !loading) {
        void loadVideos();
      }
      // Deep-link: open the matching video straight into the player. If the
      // list hasn't loaded yet, stash the flow id — the effect below resolves
      // it once the fetch completes.
      if (detail?.flowId) {
        const match = videos.find((v) => v.flow_id === detail.flowId);
        if (match) {
          openVideo(match);
        } else {
          setPendingFlowId(detail.flowId);
        }
      }
    };

    window.addEventListener(TOUR_VIDEO_LIBRARY_EVENT, handleOpen as EventListener);
    return () => window.removeEventListener(TOUR_VIDEO_LIBRARY_EVENT, handleOpen as EventListener);
  }, [loaded, loading, loadVideos, videos, openVideo]);

  // Resolve a pending deep-link once the video list arrives. When the flow
  // has no published video, say so — previously it silently did nothing.
  useEffect(() => {
    if (!pendingFlowId || !loaded) return;
    const match = videos.find((v) => v.flow_id === pendingFlowId);
    if (match) {
      openVideo(match);
    } else {
      toast('No video guide has been published for this tour yet.');
    }
    setPendingFlowId(null);
  }, [pendingFlowId, loaded, videos, openVideo]);

  // Escape closes the library (MediaViewer handles its own Escape).
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !viewerOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, viewerOpen]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return videos;
    return videos.filter(
      (v) =>
        v.title.toLowerCase().includes(query) ||
        v.description?.toLowerCase().includes(query) ||
        v.module_name?.toLowerCase().includes(query)
    );
  }, [videos, search]);

  const groups = useMemo(() => {
    const map = new Map<string, { icon: string | null; videos: TourVideoItem[] }>();
    for (const video of filtered) {
      const name = video.module_name || 'General';
      const group = map.get(name) ?? { icon: video.module_icon, videos: [] };
      group.videos.push(video);
      map.set(name, group);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        className="pointer-events-auto fixed inset-0 z-[80] flex flex-col bg-slate-950/95 backdrop-blur-sm"
        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Tour video library"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-white/10 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/20">
              <Film className="h-5 w-5 text-indigo-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Video Guides</h2>
              <p className="text-xs text-slate-400">
                Narrated walkthroughs recorded inside the product
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search guides…"
                className="w-64 rounded-lg border border-white/10 bg-white/5 py-2 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-400 focus:outline-none"
              />
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close video library"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className="border-b border-white/10 px-6 py-3 sm:hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guides…"
              className="w-full rounded-lg border border-white/10 bg-white/5 py-2 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading && (
            <div className="flex h-full items-center justify-center">
              <BrandedLoadingSpinner mode="compact" size="md" showSurface={false} showProgressBar={false} />
            </div>
          )}

          {!loading && groups.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <Film className="mb-3 h-10 w-10 text-slate-600" />
              <p className="text-sm text-slate-400">
                {search
                  ? 'No guides match your search.'
                  : 'No video guides have been published yet.'}
              </p>
            </div>
          )}

          {!loading &&
            groups.map(([moduleName, group]) => (
              <section key={moduleName} className="mb-8">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200">
                  {group.icon && <span>{group.icon}</span>}
                  {moduleName}
                  <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-slate-400">
                    {group.videos.length}
                  </span>
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {group.videos.map((video) => (
                    <div key={video.id} className="group relative">
                    <button
                      onClick={() => openVideo(video)}
                      className="w-full overflow-hidden rounded-xl border border-white/10 bg-white/5 text-left transition-all hover:border-indigo-400/60 hover:bg-white/10"
                    >
                      <div className="relative aspect-video w-full overflow-hidden bg-slate-800">
                        {video.thumbnail_url ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={video.thumbnail_url}
                            alt={video.title}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-indigo-950">
                            <Play className="h-10 w-10 text-indigo-300/60" />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30">
                          <Play className="h-10 w-10 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                        {video.duration ? (
                          <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
                            <Clock className="h-3 w-3" />
                            {formatDuration(video.duration)}
                          </span>
                        ) : null}
                      </div>
                      <div className="p-3">
                        <h4 className="line-clamp-1 text-sm font-semibold text-white">
                          {video.title}
                        </h4>
                        {video.description && (
                          <p className="mt-1 line-clamp-2 text-xs text-slate-400">
                            {video.description}
                          </p>
                        )}
                      </div>
                    </button>
                    {mediaRegenAvailable && video.module_key && video.flow_id && (
                      <button
                        onClick={async () => {
                          const key = `${video.module_key}/${video.flow_id}`;
                          setRegenKey(key);
                          const result = await requestTourMediaRegen({
                            moduleKey: video.module_key!,
                            flowId: video.flow_id!,
                          });
                          if (result.success) {
                            toast.success(result.message ?? `Regeneration queued for ${video.title}`);
                          } else {
                            toast.error(result.error ?? 'Failed to queue regeneration');
                          }
                          setRegenKey(null);
                        }}
                        disabled={regenKey === `${video.module_key}/${video.flow_id}`}
                        className="absolute right-2 top-2 z-10 rounded-lg bg-black/70 p-1.5 text-slate-300 opacity-0 transition-opacity hover:bg-black/90 hover:text-white group-hover:opacity-100 focus:opacity-100"
                        title="Re-record this video & narration (dev only)"
                        aria-label={`Regenerate ${video.title}`}
                      >
                        <RefreshCw
                          className={
                            regenKey === `${video.module_key}/${video.flow_id}`
                              ? 'h-3.5 w-3.5 animate-spin'
                              : 'h-3.5 w-3.5'
                          }
                        />
                      </button>
                    )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
        </div>
      </div>

      {/* Shared media player — same component used by /resources */}
      <MediaViewer
        isOpen={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setSelectedMedia(null);
        }}
        media={selectedMedia}
        isDark={true}
        permissions={{
          can_edit: false,
          can_delete: false,
          can_publish_resources: false,
          can_share: false,
        }}
      />
    </>
  );
}

export default GuidedTourVideoLibrary;
